(() => {
  'use strict';
  const MAX_LISTS = 20, MAX_ITEMS = 150, MAX_BYTES = 125000;
  const clone = value => JSON.parse(JSON.stringify(value));
  const digits = value => /^\d{1,20}$/.test(String(value || '')) ? String(value) : '';
  const id = value => /^[a-zA-Z0-9_-]{1,70}$/.test(String(value || '')) ? String(value) : '';
  const name = value => String(value || '').trim().replace(/\s+/g, ' ').slice(0, 80);
  const image = value => { if (!value) return ''; try { const u = new URL(value, 'https://prettypolish.nl'); return u.protocol === 'https:' && ['prettypolish.nl', 'www.prettypolish.nl', 'cdn.shopify.com'].includes(u.hostname) ? u.href.slice(0, 800) : ''; } catch { return ''; } };
  function singleProduct(raw) {
    const productId = digits(raw?.productId), handle = String(raw?.handle || '');
    if (!productId || !/^[a-z0-9][a-z0-9-]{0,240}$/.test(handle)) return null;
    return { productId, handle, variantId: digits(raw.variantId), qty: Number.isInteger(Number(raw.qty)) && Number(raw.qty) > 0 ? Math.min(9999, Number(raw.qty)) : 1, title: String(raw.title || handle).slice(0, 240), image: image(raw.image) };
  }
  const key = item => item.bundle ? 'set-' + item.bundle.id : item.productId;
  const components = item => item.bundle ? item.bundle.items : [item];
  function product(raw) {
    const item = singleProduct(raw);
    if (!item) return null;
    if (raw.bundle) {
      const bundle = raw.bundle;
      if (!id(bundle.id) || !['gel', 'stickers'].includes(bundle.type) || !Array.isArray(bundle.items) || !bundle.items.length || bundle.items.length > 250) throw new Error('invalid_data');
      const items = bundle.items.map(singleProduct);
      if (items.some((part, index) => !part || !part.variantId || bundle.items[index].bundle)) throw new Error('invalid_data');
      const merged = new Map();
      for (const part of items) {
        const previous = merged.get(part.variantId);
        if (previous && previous.productId !== part.productId) throw new Error('invalid_data');
        if (previous) previous.qty += part.qty; else merged.set(part.variantId, part);
      }
      if ([...merged.values()].some(part => part.qty > 9999)) throw new Error('invalid_data');
      item.qty = 1;
      item.bundle = { id: bundle.id, type: bundle.type, items: [...merged.values()] };
    }
    return item;
  }
  function cartItems(item, groupId, created, setLabel, root = '/') {
    if (!item.bundle) return [{ id: item.variantId, quantity: item.qty }];
    if (!id(groupId)) throw new Error('invalid_item');
    const properties = { _pp_starter_id: groupId, _pp_starter_type: item.bundle.type, _pp_starter_name: item.title, _pp_starter_created: String(created), _pp_starter_url: root + 'products/' + item.handle, [setLabel]: item.title };
    return item.bundle.items.map(part => ({ id: part.variantId, quantity: part.qty, properties: { ...properties } }));
  }
  const cartKey = item => String(item.variant_id || item.id) + '|' + JSON.stringify(Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b))) + '|' + String(item.selling_plan_allocation?.selling_plan?.id || '');
  function normalise(raw) {
    if (raw && (![1, 2].includes(raw.version) || !Array.isArray(raw.lists))) throw new Error('invalid_data');
    if (raw?.lists?.length > MAX_LISTS || raw?.lists?.some(list => Array.isArray(list.items) && list.items.length > MAX_ITEMS)) throw new Error('storage_limit');
    const result = { version: 1, lists: [] }, ids = new Set();
    for (const entry of Array.isArray(raw?.lists) ? raw.lists.slice(0, MAX_LISTS) : []) {
      if (!id(entry?.id) || ids.has(entry.id) || !name(entry.name)) continue;
      ids.add(entry.id); const seen = new Set(), items = [];
      for (const rawItem of Array.isArray(entry.items) ? entry.items.slice(0, MAX_ITEMS) : []) { const item = product(rawItem); if (item && !seen.has(key(item))) { seen.add(key(item)); items.push(item); } }
      result.lists.push({ id: entry.id, name: name(entry.name), createdAt: String(entry.createdAt || '').slice(0, 30), updatedAt: String(entry.updatedAt || '').slice(0, 30), items });
    }
    if (result.lists.some(list => list.items.some(item => item.bundle))) result.version = 2;
    return result;
  }
  function apply(source, action) {
    const data = normalise(source), stamp = action.at || new Date().toISOString();
    const list = data.lists.find(l => l.id === action.listId);
    if (action.type === 'create') {
      if (!id(action.listId) || !name(action.name)) throw new Error('invalid_name');
      if (!list) {
        if (data.lists.length >= MAX_LISTS) throw new Error('list_limit');
        const items = [...new Map((action.products || []).map(product).filter(Boolean).map(item => [key(item), item])).values()];
        if (items.length > MAX_ITEMS) throw new Error('item_limit');
        data.lists.push({ id: action.listId, name: name(action.name), createdAt: stamp, updatedAt: stamp, items });
      }
    } else if (action.type === 'rename') { if (!list) throw new Error('list_missing'); if (!name(action.name)) throw new Error('invalid_name'); list.name = name(action.name); list.updatedAt = stamp; }
    else if (action.type === 'delete') data.lists = data.lists.filter(l => l.id !== action.listId);
    else if (action.type === 'membership') {
      const selected = new Set(action.listIds || []), products = (action.products || []).map(product).filter(Boolean);
      if (![...selected].every(value => data.lists.some(l => l.id === value))) throw new Error('list_missing');
      data.lists.forEach(l => {
        for (const item of products) {
          const index = l.items.findIndex(p => key(p) === key(item));
          if (selected.has(l.id) && index < 0) l.items.push(item);
          if (selected.has(l.id) && index >= 0 && action.replaceExisting === true) l.items[index] = item;
          if (!selected.has(l.id) && !action.addOnly && index >= 0) l.items.splice(index, 1);
        }
        if (l.items.length > MAX_ITEMS) throw new Error('item_limit');
        l.updatedAt = stamp;
      });
    } else if (action.type === 'item') {
      if (!list) throw new Error('list_missing'); const index = list.items.findIndex(p => key(p) === action.productId);
      if (index < 0) throw new Error('item_missing');
      if (action.remove) list.items.splice(index, 1);
      else { const item = product({ ...list.items[index], ...action.patch }); if (!item) throw new Error('invalid_item'); list.items[index] = item; }
      list.updatedAt = stamp;
    } else if (action.type === 'import') {
      for (const entry of normalise(action.data).lists) { if (!data.lists.some(l => l.id === entry.id)) data.lists.push(clone(entry)); }
      if (data.lists.length > MAX_LISTS) throw new Error('list_limit');
    } else throw new Error('invalid_action');
    if (data.lists.some(list => list.items.some(item => item.bundle))) data.version = 2;
    if (new TextEncoder().encode(JSON.stringify(data)).length > MAX_BYTES) throw new Error('storage_limit');
    return data;
  }
  globalThis.PPListsModel = { normalise, product, apply, clone, key, components, cartItems, cartKey };
})();
